import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class BHCompleteLook extends HTMLElement {
  #abort;
  #cartIds = new Set();
  #labelTimers = new WeakMap();
  #busy = false;
  #impressionSent = false;
  #observer;
  #cartRevision = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#cartIds = new Set((this.dataset.cartProducts || '').split(',').filter(Boolean));
    this.addEventListener('submit', this.#submit, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('click', this.#click, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdate, options);
    this.#applyCart();
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !this.#impressionSent && !this.hidden) {
        this.#impressionSent = true;
        this.#publish('impression', { recommended_products: this.#visibleCards().map((card) => card.dataset.productHandle) });
        this.#observer.disconnect();
      }
    }, { threshold: 0.5 });
    this.#observer.observe(this);
    this.#fetchCart().catch(() => {});
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #cartUrl(path) {
    const root = (this.dataset.root || '/').replace(/\/?$/, '/');
    return `${root}${path}`;
  }

  #setButtonLabel(button, label) {
    (button.querySelector('[data-look-add-label]') || button).textContent = label;
  }

  #visibleCards() {
    return [...this.querySelectorAll('[data-look-card][data-visible]')];
  }

  #applyCart() {
    let count = 0;
    for (const card of this.querySelectorAll('[data-look-card]')) {
      const show = count < 2;
      card.hidden = !show;
      card.toggleAttribute('data-visible', show);
      if (show) count++;

    }
    this.hidden = count === 0;
  }

  #setCart(cart) {
    if (!Array.isArray(cart?.items)) return;
    this.#cartIds = new Set(cart.items.map((item) => String(item.product_id)));
    this.#applyCart();
  }

  async #fetchCart() {
    const revision = this.#cartRevision;
    const response = await fetch(this.#cartUrl('cart.js'), { headers: { Accept: 'application/json' }, signal: this.#abort.signal });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (revision === this.#cartRevision) this.#setCart(cart);
    return cart;
  }

  #cartUpdate = (event) => {
    if (!event.promise) return;
    this.#cartRevision++;
    event.promise.then(({ detail }) => {
      if (detail?.items) this.#setCart({ items: detail.items });
      else this.#fetchCart().catch(() => {});
    }).catch(() => {});
  };

  #change = (event) => {
    const select = event.target.closest('select[name="id"]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const card = select.closest('[data-look-card]');
    card.querySelector('[data-look-price]').textContent = option.dataset.price || '';
    const compare = card.querySelector('[data-look-compare]');
    compare.textContent = option.dataset.compare || '';
    compare.hidden = option.dataset.onSale !== 'true';
    this.querySelector('.bh-look__status').textContent = '';
    const button = card.querySelector('[data-look-add]');
    if (card.querySelector('[data-look-form]').getAttribute('aria-busy') !== 'true') {
      clearTimeout(this.#labelTimers.get(button));
      this.#setButtonLabel(button, this.dataset.addLabel);
    }
  };

  #click = (event) => {
    const link = event.target.closest('a');
    const card = link?.closest('[data-look-card]');
    if (card) this.#publish('product_click', { recommended_product: card.dataset.productHandle, position: Number(card.dataset.position) });
  };

  #publish(type, detail) {
    const payload = { source_product: this.dataset.productHandle, feature_version: 'v1', ...detail };
    this.dispatchEvent(new CustomEvent(`brokenheart:complete_look_${type}`, { bubbles: true, detail: payload }));
    try {
      Promise.resolve(window.Shopify?.analytics?.publish?.(`brokenheart:complete_look_${type}`, payload)).catch(() => {});
    } catch {}
  }

  #submit = async (event) => {
    const form = event.target.closest('[data-look-form]');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#busy) return;
    const data = new FormData(form);
    const variantId = data.get('id');
    const status = this.querySelector('.bh-look__status');
    if (!variantId) {
      status.textContent = this.dataset.selectLabel;
      form.querySelector('select')?.focus();
      return;
    }
    this.#busy = true;
    const button = form.querySelector('[data-look-add]');
    const card = form.closest('[data-look-card]');
    clearTimeout(this.#labelTimers.get(button));
    button.disabled = true;
    this.#setButtonLabel(button, this.dataset.addingLabel);
    form.setAttribute('aria-busy', 'true');
    status.textContent = '';
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
    const properties = { _complete_look_source: this.dataset.productHandle, _complete_look_version: 'v1' };
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(variantId), quantity: 1 }], promise: deferred.promise }));
    let added = false;
    try {
      const response = await fetch(this.#cartUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties }], sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorLabel);
      added = true;
      this.#publish('add', { recommended_product: card.dataset.productHandle, variant_id: String(variantId), position: Number(card.dataset.position) });
      this.#setButtonLabel(button, this.dataset.addedLabel);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) button.querySelector('.bh-look__cart-icon')?.animate?.([
        { transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }
      ], { duration: 350, easing: 'ease-out' });
      try {
        const cart = await this.#fetchCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: result.sections, source: 'bh-complete-look', productId: card.dataset.productId, itemCount: 1, didError: false } });
      } catch {
        deferred.resolve({ cart: null, detail: { sections: result.sections, source: 'bh-complete-look', didError: false } });
        this.#cartIds.add(card.dataset.productId);
        this.#applyCart();

      }
    } catch (error) {
      deferred.resolve({ cart: null, detail: { didError: true, source: 'bh-complete-look' } });
      status.textContent = error.message || this.dataset.errorLabel;
      this.#publish('error', { recommended_product: card.dataset.productHandle });
    } finally {
      this.#busy = false;
      form.removeAttribute('aria-busy');
      button.disabled = false;
      if (added) {
        this.#labelTimers.set(button, setTimeout(() => {
          if (button.isConnected) this.#setButtonLabel(button, this.dataset.addLabel);
        }, 1500));
      } else {
        this.#setButtonLabel(button, this.dataset.addLabel);
      }
    }
  };
}

if (!customElements.get('bh-complete-look')) customElements.define('bh-complete-look', BHCompleteLook);
